import { defineStore } from 'pinia'
import { ref } from 'vue'
import client from '../api/client.js'

/**
 * Group-Buy store — wired to the backend marketplace/groupbuy module
 * (GroupBuyController @RequestMapping("/marketplace/group-buy")).
 *
 * Enum codes match GroupBuyStatus verbatim:
 *   DRAFT | ACTIVE | TARGET_REACHED | EXPIRED | ALLOCATING
 *   | SUCCESSFUL | FAILED | CANCELLED
 */

export const GROUP_BUY_STATUS_META = {
  DRAFT: { labelKey: 'groupBuy.statusDraft', color: '#8e8e93' },
  ACTIVE: { labelKey: 'groupBuy.statusActive', color: '#0e7490' },
  TARGET_REACHED: { labelKey: 'groupBuy.statusTargetReached', color: '#1a6b42' },
  EXPIRED: { labelKey: 'groupBuy.statusExpired', color: '#c0392b' },
  ALLOCATING: { labelKey: 'groupBuy.statusAllocating', color: '#1a6b42' },
  SUCCESSFUL: { labelKey: 'groupBuy.statusSuccessful', color: '#1a6b42' },
  FAILED: { labelKey: 'groupBuy.statusFailed', color: '#c0392b' },
  CANCELLED: { labelKey: 'groupBuy.statusCancelled', color: '#8e8e93' },
}

/** Suggest participant-based tier pricing from a starting unit price. */
export function suggestGroupBuyTiers(startingPrice) {
  const price = Number(startingPrice) || 0
  return [
    { minimumParticipants: 2, unitPrice: price, sortOrder: 1 },
    { minimumParticipants: 5, unitPrice: Math.round(price * 0.9), sortOrder: 2 },
    { minimumParticipants: 10, unitPrice: Math.round(price * 0.82), sortOrder: 3 },
  ]
}

/** Lowest unit price reachable once `participants` have joined. */
export function groupBuyUnitPrice(tiers, participants) {
  const sorted = [...(tiers || [])]
    .filter((t) => Number.isFinite(Number(t.unitPrice)))
    .sort((a, b) => a.minimumParticipants - b.minimumParticipants)
  const matched = [...sorted].reverse().find((t) => participants >= t.minimumParticipants)
  return matched ? matched.unitPrice : sorted[0]?.unitPrice
}

export function groupBuyProductPrice(product) {
  const value =
    product?.price ??
    product?.basePrice ??
    product?.currentPrice ??
    product?.unitPrice ??
    null
  return value == null || value === '' ? null : Number(value)
}

function localId(prefix) {
  return `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
}

function isNetworkError(error) {
  return !!error && !error.response
}

export const useGroupBuyStore = defineStore('groupBuy', () => {
  const campaigns = ref([])
  const activeCampaign = ref(null)
  const tiers = ref([])
  const loading = ref(false)
  const groupCampaigns = ref([])
  const eligibleProducts = ref([])

  function upsertCampaign(campaign) {
    if (!campaign?.id) return
    const index = campaigns.value.findIndex((c) => c.id === campaign.id)
    if (index === -1) campaigns.value.unshift(campaign)
    else campaigns.value[index] = campaign
  }

  // ── Campaign lifecycle ───────────────────────────────────────────

  async function getCampaign(id) {
    loading.value = true
    try {
      const { data } = await client.get(`marketplace/group-buy/campaigns/${id}`)
      upsertCampaign(data)
      activeCampaign.value = data
      return data
    } catch (error) {
      const local = campaigns.value.find((c) => c.id === id)
      if (local) {
        activeCampaign.value = local
        return local
      }
      throw error
    } finally {
      loading.value = false
    }
  }

  async function getActiveCampaigns() {
    try {
      const { data } = await client.get('marketplace/group-buy/campaigns/active')
      const list = Array.isArray(data) ? data : []
      list.forEach(upsertCampaign)
      return list
    } catch {
      return campaigns.value.filter((c) => c.status === 'ACTIVE')
    }
  }

  async function getCampaignsByProduct(productId) {
    try {
      const { data } = await client.get(
        `marketplace/group-buy/campaigns/product/${productId}`,
      )
      const list = Array.isArray(data) ? data : []
      list.forEach(upsertCampaign)
      return list
    } catch {
      return campaigns.value.filter((c) => String(c.productId) === String(productId))
    }
  }

  async function createCampaign(payload) {
    try {
      const { data } = await client.post('marketplace/group-buy/campaigns', payload)
      upsertCampaign(data)
      activeCampaign.value = data
      return data
    } catch (error) {
      if (!isNetworkError(error)) throw error
      const campaign = {
        id: localId('gb'),
        ...payload,
        status: 'DRAFT',
        createdAt: new Date().toISOString(),
      }
      upsertCampaign(campaign)
      activeCampaign.value = campaign
      return campaign
    }
  }

  async function updateCampaign(id, payload) {
    const { data } = await client.put(
      `marketplace/group-buy/campaigns/${id}`,
      payload,
    )
    upsertCampaign(data)
    activeCampaign.value = data
    return data
  }

  async function campaignAction(path) {
    const { data } = await client.post(`marketplace/group-buy/campaigns/${path}`)
    if (data) {
      upsertCampaign(data)
      activeCampaign.value = data
    }
    return data
  }

  function activateCampaign(id) {
    return campaignAction(`${id}/activate`)
  }
  function cancelCampaign(id) {
    return campaignAction(`${id}/cancel`)
  }
  function markCampaignExpired(id) {
    return campaignAction(`${id}/expire`)
  }
  function markTargetReached(id) {
    return campaignAction(`${id}/target-reached`)
  }
  function startAllocation(id) {
    return campaignAction(`${id}/allocation/start`)
  }
  function markCampaignSuccessful(id) {
    return campaignAction(`${id}/success`)
  }
  function markCampaignFailed(id) {
    return campaignAction(`${id}/fail`)
  }

  // ── Tiers ────────────────────────────────────────────────────────

  async function addTier({ campaignId, minimumParticipants, unitPrice, sortOrder }) {
    const { data } = await client.post(
      `marketplace/group-buy/campaigns/${campaignId}/tiers`,
      {
        campaignId,
        minimumParticipants,
        unitPrice,
        sortOrder: sortOrder ?? 0,
      },
    )
    const index = tiers.value.findIndex((t) => t.id === data?.id)
    if (index === -1) tiers.value.push(data)
    else tiers.value[index] = data
    return data
  }

  async function getTiers(campaignId) {
    try {
      const { data } = await client.get(
        `marketplace/group-buy/campaigns/${campaignId}/tiers`,
      )
      tiers.value = Array.isArray(data) ? data : []
      return tiers.value
    } catch {
      tiers.value = []
      return []
    }
  }

  async function updateTier(tierId, payload) {
    const { data } = await client.put(
      `marketplace/group-buy/tiers/${tierId}`,
      payload,
    )
    const index = tiers.value.findIndex((t) => t.id === tierId)
    if (index !== -1) tiers.value[index] = data
    return data
  }

  async function deleteTier(tierId) {
    await client.delete(`marketplace/group-buy/tiers/${tierId}`)
    tiers.value = tiers.value.filter((t) => t.id !== tierId)
    return true
  }

  return {
    campaigns,
    activeCampaign,
    tiers,
    loading,
    groupCampaigns,
    eligibleProducts,
    getCampaign,
    getActiveCampaigns,
    getCampaignsByProduct,
    createCampaign,
    updateCampaign,
    activateCampaign,
    cancelCampaign,
    markCampaignExpired,
    markTargetReached,
    startAllocation,
    markCampaignSuccessful,
    markCampaignFailed,
    addTier,
    getTiers,
    updateTier,
    deleteTier,
  }
})